/**
 * The clearing in the game (decision 0015): what has been found, in the clearing's
 * cell of the HUD, and a card for each new find that slides out under the cell with the thing's
 * picture, "New find" and its name, then back into the cell as the count there goes up, one at a
 * time (Daniel, 2026-10-18). Several finds from one reveal share a card: the first and "+N".
 */
import type { StringKey } from "../i18n.ts";
import { bind, esc } from "./dom.ts";
import "./finds.css";

type T = (key: StringKey, params?: Record<string, string | number>) => string;

export interface FindNote {
  /** The thing's name as the clearing's tray gives it. */
  readonly label: string;
  /** Its picture, drawn when its turn comes; null without one. */
  readonly picture: Promise<string | null>;
}

/** How long a card stays to be read. */
const HOLD_MS = 2200;
/** How long a picture is waited for before the card shows without it; it comes in later. */
const PICTURE_MS = 400;
/** The card's way out and back in, as finds.css moves it. */
const IN_MS = 280;
const OUT_MS = 240;
/** Each find counted up, one after another. */
const STEP_MS = 140;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export class FindNotes {
  private shown = 0;
  private target = 0;
  private total = 0;
  private busy = false;
  private readonly queue: { readonly notes: readonly FindNote[]; readonly upTo: number }[] = [];
  private readonly hud: HTMLElement;
  private readonly t: T;
  private readonly reduced: () => boolean;
  /** A card came out, for its sound. */
  private readonly onCard: () => void;

  constructor(hud: HTMLElement, t: T, reduced: () => boolean, onCard: () => void) {
    this.hud = hud;
    this.t = t;
    this.reduced = reduced;
    this.onCard = onCard;
  }

  /** What has been found, shown at once unless cards are still counting up to it. */
  setFound(found: number, total: number): void {
    this.target = found;
    this.total = total;
    if (this.busy) return;
    this.shown = found;
    this.paint();
  }

  /** New finds: a card for them, then the count up to `found`. */
  note(notes: readonly FindNote[], found: number, total: number): void {
    if (notes.length === 0) {
      this.setFound(found, total);
      return;
    }
    this.target = found;
    this.total = total;
    // With nothing waiting, the count shows what there was before these finds while they show.
    if (!this.busy && this.queue.length === 0) {
      this.shown = Math.max(0, found - notes.length);
      this.paint();
    }
    this.queue.push({ notes, upTo: found });
    void this.run();
  }

  private paint(): void {
    bind(this.hud, "found", `${this.shown}/${this.total}`);
  }

  private async run(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      for (let batch = this.queue.shift(); batch; batch = this.queue.shift()) {
        await this.card(batch.notes);
        await this.countTo(batch.upTo);
      }
    } finally {
      this.busy = false;
      if (this.shown !== this.target) {
        this.shown = this.target;
        this.paint();
      }
    }
  }

  /** The clearing's cell on screen: the phone's or the desktop's, whichever shows. */
  private cell(): HTMLElement | null {
    for (const el of this.hud.querySelectorAll<HTMLElement>('[data-action="clearing"]')) {
      if (el.offsetParent !== null) return el;
    }
    return null;
  }

  private async card(notes: readonly FindNote[]): Promise<void> {
    const first = notes[0];
    if (!first) return;
    // The picture first: a run set out from the start sheet shows the HUD only after its finds.
    const url = await Promise.race([first.picture, wait(PICTURE_MS).then(() => null)]);
    const cell = this.cell();
    if (!cell) return;
    const more = notes.length - 1;
    const card = document.createElement("div");
    card.className = "find-card entering";
    card.setAttribute("role", "status");
    card.innerHTML = `<span class="find-thumb"></span><span class="find-text"><span class="kicker">${esc(this.t("find.new"))}${more > 0 ? ` +${more}` : ""}</span><strong>${esc(first.label)}</strong></span>`;
    const box = cell.getBoundingClientRect();
    card.style.top = `${Math.round(box.bottom + 8)}px`;
    card.style.right = `${Math.max(8, Math.round(innerWidth - box.right))}px`;
    const thumb = card.querySelector(".find-thumb") as HTMLElement;
    const put = (url: string | null) => {
      if (!url || thumb.firstChild) return;
      const img = new Image();
      img.alt = "";
      img.src = url;
      thumb.append(img);
    };
    put(url);
    void first.picture.then(put);
    this.hud.append(card);
    this.onCard();
    // Out from under the cell: the card's first frame stands where `entering` puts it.
    void card.offsetWidth;
    card.classList.remove("entering");
    const quiet = this.reduced();
    await wait(quiet ? HOLD_MS : IN_MS + HOLD_MS);
    card.classList.add("leaving");
    await wait(quiet ? 0 : OUT_MS);
    card.remove();
  }

  private async countTo(upTo: number): Promise<void> {
    const cell = this.cell();
    while (this.shown < Math.min(upTo, this.target)) {
      this.shown += 1;
      this.paint();
      if (cell && !this.reduced()) {
        cell.classList.remove("bump");
        void cell.offsetWidth;
        cell.classList.add("bump");
        await wait(STEP_MS);
      }
    }
  }
}
